<template>
  <div id="page-container">
    <div class="page-section jobs_container">
      <div :class="containerClass">
        <div class="cert-header">
          <div>
            <h2 class="mb-1">Certifications</h2>
            <p class="cert-sub mb-0">Certification programs offered by your organization's training providers.</p>
          </div>
          <form class="search-form search-form--light cert-search" @submit.prevent="">
            <b-form-input placeholder="Search certifications" v-model="searchTerm" @input="onSearch" />
            <b-btn variant="flush" type="submit" aria-label="Search">
              <md-icon v-text="'search'" />
            </b-btn>
          </form>
        </div>

        <p class="cert-count" v-if="!isLoading && totalCertifications">
          {{ totalCertifications }} certification{{ totalCertifications !== 1 ? 's' : '' }}
        </p>

        <div class="d-flex justify-content-center" v-if="!totalCertifications && !isLoading">
          <div class="cert-empty">
            <h4 class="mb-1">No certifications found</h4>
            <p class="mb-0">No certification programs are available from your organization's training providers yet.</p>
          </div>
        </div>

        <b-skeleton-wrapper :loading="isLoading" v-else>
          <template #loading>
            <div class="row">
              <div class="col-md-6 col-lg-4 mb-4" v-for="item in [1, 2, 3]" :key="item">
                <div class="cert-card">
                  <b-skeleton-img no-aspect height="150px" />
                  <div class="cert-body">
                    <b-skeleton width="60%"></b-skeleton>
                    <b-skeleton width="100%"></b-skeleton>
                    <b-skeleton width="90%"></b-skeleton>
                  </div>
                </div>
              </div>
            </div>
          </template>

          <div class="row" id="certifications-list">
            <div class="col-md-6 col-lg-4 mb-4" v-for="cert in certifications" :key="cert.slug || cert.title">
              <div class="cert-card">
                <div class="cert-photo">
                  <img :src="cert.image_url" v-if="cert.image_url" alt="" />
                </div>
                <div class="cert-body">
                  <h3 class="cert-title">{{ cert.title }}</h3>
                  <p class="cert-desc">{{ cert.description }}</p>
                  <div class="cert-foot">
                    <template v-if="schoolsOf(cert).length === 1">
                      <a
                        :href="enrollLink(schoolsOf(cert)[0], cert)"
                        target="_blank"
                        rel="noopener"
                        class="btn btn-primary btn-sm cert-btn"
                      >
                        Enroll at {{ schoolsOf(cert)[0].name }}
                      </a>
                    </template>
                    <template v-else-if="schoolsOf(cert).length > 1">
                      <span class="cert-offered">Offered by {{ schoolsOf(cert).length }} schools</span>
                      <b-btn variant="primary" size="sm" class="cert-btn" @click="openSchools(cert)">See schools</b-btn>
                    </template>
                    <span v-else class="cert-offered">Enrollment coming soon</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </b-skeleton-wrapper>

        <pagination
          v-if="totalCertifications > certifications.length"
          v-model="currentPage"
          :total-rows="totalCertifications"
          :per-page="perPage"
          @change="onPageChange"
          aria-controls="certifications-list"
        />
      </div>
    </div>

    <b-modal
      id="cert-schools-modal"
      :title="selectedCert ? selectedCert.title : ''"
      hide-footer
      centered
      scrollable
      body-class="p-0"
    >
      <p class="cert-modal-sub" v-if="selectedCert">
        {{ schoolsOf(selectedCert).length }} schools offer this certification. Pick one to enroll.
      </p>
      <ul class="cert-school-list" v-if="selectedCert">
        <li v-for="school in schoolsOf(selectedCert)" :key="school.slug || school.name">
          <span class="cert-school-name">{{ school.name }}</span>
          <a :href="enrollLink(school, selectedCert)" target="_blank" rel="noopener" class="btn btn-outline-primary btn-sm">
            Enroll
          </a>
        </li>
      </ul>
    </b-modal>
  </div>
</template>

<script>
import { debounce, get } from 'lodash';
import Page from '@/components/Page.vue';
import Pagination from '../../../components/Ui/Pagination.vue';
import { mapActions } from 'vuex';

export default {
  components: { Pagination },
  extends: Page,

  data() {
    return {
      title: 'Certifications',
      isLoading: true,
      perPage: 12,
      currentPage: 1,
      totalCertifications: 0,
      certifications: [],
      searchTerm: '',
      ordering: 'title',
      pageFromCount: 0,
      pageToCount: 0,
      selectedCert: null,
    };
  },

  methods: {
    ...mapActions('program', ['getStudentCertifications']),
    get,

    async fetchCertifications(pageNum = 1, params = {}) {
      this.isLoading = true;
      if (document.getElementById('page-container')) {
        document.getElementById('page-container').scrollIntoView();
      }
      const response = await this.getStudentCertifications({
        limit: this.perPage,
        offset: (pageNum - 1) * this.perPage,
        ...(this.ordering && { ordering: this.ordering }),
        ...(this.searchTerm && { search: this.searchTerm }),
        ...params,
      });

      this.certifications = response.data.results;
      this.currentPage = pageNum;
      this.totalCertifications = response.data.count;
      this.pageFromCount = (this.currentPage - 1) * this.perPage + 1;
      this.pageToCount = this.pageFromCount + get(response.data, 'results.length', 0) - 1;
      this.isLoading = false;
    },

    schoolsOf(cert) {
      return get(cert, 'enroll_schools') || [];
    },

    enrollLink(school, cert) {
      return `${school.enrollment_link}/programs/${cert.slug}/`;
    },

    openSchools(cert) {
      this.selectedCert = cert;
      this.$bvModal.show('cert-schools-modal');
    },

    onPageChange(pageNum) {
      this.fetchCertifications(pageNum);
    },

    onSearch() {
      this.debouncedSearch(this);
    },

    debouncedSearch: debounce((vm) => {
      vm.fetchCertifications();
    }, 500),
  },

  async mounted() {
    this.fetchCertifications();
  },
};
</script>

<style lang="scss" scoped>
.cert-header {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px 24px;
  margin-bottom: 8px;
}

.cert-sub {
  color: #58615c;
  font-size: 15px;
}

.cert-search {
  width: 280px;
  max-width: 100%;
}

.cert-count {
  color: #58615c;
  font-size: 14px;
  margin: 8px 0 20px;
}

.cert-empty {
  background: #ffffff;
  border-radius: 14px;
  padding: 40px 24px;
  text-align: center;
  color: #58615c;
  max-width: 560px;
}

.cert-card {
  background: #ffffff;
  border-radius: 14px;
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
  box-shadow: 0 1px 2px rgba(20, 28, 40, 0.06), 0 8px 24px -12px rgba(20, 28, 40, 0.18);
}

.cert-photo {
  height: 150px;
  background: #ececec;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
}

.cert-body {
  padding: 18px 20px 20px;
  display: flex;
  flex-direction: column;
  flex: 1;
}

.cert-title {
  font-size: 17px;
  font-weight: 700;
  color: #181818;
  text-transform: none;
  letter-spacing: 0;
  line-height: 1.3;
  margin: 0 0 6px;
}

.cert-desc {
  color: #58615c;
  font-size: 14px;
  line-height: 1.45;
  margin: 0 0 16px;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.cert-foot {
  margin-top: auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.cert-offered {
  color: #58615c;
  font-size: 13px;
}

.cert-btn {
  white-space: normal;
  text-align: left;
}

.cert-modal-sub {
  color: #58615c;
  font-size: 14px;
  margin: 0;
  padding: 16px 20px 8px;
}

.cert-school-list {
  list-style: none;
  margin: 0;
  padding: 0 0 8px;

  li {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 20px;
    border-top: 1px solid #eef0f2;
  }
}

.cert-school-name {
  font-weight: 500;
  color: #181818;
}
</style>
